/**
 * GRANULLO PLATFORM EVOLUTION — System Blueprint Context
 * 
 * Phase 2: System Blueprint Graph Model
 * 
 * CANONICAL TOPOLOGY LAYER — Derived from ProgramIntegrityContext.
 * 
 * FOUNDER DIRECTIVE: SINGLE SOURCE OF TRUTH REQUIREMENT
 * - ProgramIntegrityContext is the canonical owner
 * - SystemBlueprintContext is DERIVED from ProgramIntegrityContext
 * - No independent writable shadow state may exist
 * 
 * This graph model powers:
 * - Cascade Engine (propagation paths)
 * - Exposure Engine (risk evaluation)
 * - Simulation Engine (scenario execution)
 * - Monitoring Layer (signal attachment points)
 * - Commit Economy Engine (topology-derived commits)
 * 
 * ARCHITECTURE CONSTRAINT: No duplicate topology models may exist.
 * All topology data MUST flow through ProgramIntegrityContext.
 * 
 * Blueprint Graph Structure:
 * - Nodes: Participants (institutions, people, accounts)
 * - Edges: Flows and dependencies (money, data, authority)
 * - Layers: Logical groupings for visualization
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useProgramIntegrityOptional } from './ProgramIntegrityContext';

// ============================================================================
// NODE TYPE DEFINITIONS
// ============================================================================

/**
 * Blueprint Node Types — All participant types across program categories
 */
export const NODE_TYPES = {
  // Financial Infrastructure
  CENTRAL_BANK: 'central_bank',
  COMMERCIAL_BANK: 'commercial_bank',
  
  // Program Participants
  SPONSOR: 'sponsor',
  PROGRAM_SPONSOR: 'program_sponsor',
  BENEFICIARY: 'beneficiary',
  MERCHANT: 'merchant',
  
  // Authority Nodes
  AUTHORITY_NODE: 'authority_node',
  REGULATOR: 'regulator',
  AUDITOR: 'auditor',
  
  // Donation/CSR
  DONOR: 'donor',
  FUND_POOL: 'fund_pool',
  CAUSE: 'cause',
  EXECUTION_PARTNER: 'execution_partner',
  
  // Circularity/DPP
  WASTE_PRODUCER: 'waste_producer',
  COLLECTOR: 'collector',
  RECYCLER: 'recycler',
  BUYER: 'buyer',
  
  // Signal Sources
  SIGNAL_SOURCE: 'signal_source',
  TELEMETRY_NODE: 'telemetry_node',
  
  // Generic
  CUSTOM: 'custom'
};

/**
 * Node Type Metadata — Display names and categories
 */
export const NODE_TYPE_METADATA = {
  [NODE_TYPES.CENTRAL_BANK]: { 
    label: 'Central Bank', 
    category: 'financial', 
    icon: 'building-columns',
    description: 'Issuance authority for programmable money'
  },
  [NODE_TYPES.COMMERCIAL_BANK]: { 
    label: 'Commercial Bank', 
    category: 'financial', 
    icon: 'landmark',
    description: 'Distribution and settlement layer'
  },
  [NODE_TYPES.SPONSOR]: { 
    label: 'Sponsor', 
    category: 'participant', 
    icon: 'user-tie',
    description: 'Program initiator and policy owner'
  },
  [NODE_TYPES.PROGRAM_SPONSOR]: { 
    label: 'Program Sponsor', 
    category: 'participant', 
    icon: 'briefcase',
    description: 'Entity sponsoring the program'
  },
  [NODE_TYPES.BENEFICIARY]: { 
    label: 'Beneficiary', 
    category: 'participant', 
    icon: 'user',
    description: 'End recipient of program benefits'
  },
  [NODE_TYPES.MERCHANT]: { 
    label: 'Merchant', 
    category: 'participant', 
    icon: 'store',
    description: 'Service or goods provider'
  },
  [NODE_TYPES.AUTHORITY_NODE]: { 
    label: 'Authority Node', 
    category: 'authority', 
    icon: 'shield-check',
    description: 'Decision authority in governance chain'
  },
  [NODE_TYPES.REGULATOR]: { 
    label: 'Regulator', 
    category: 'authority', 
    icon: 'gavel',
    description: 'Regulatory oversight authority'
  },
  [NODE_TYPES.AUDITOR]: { 
    label: 'Auditor', 
    category: 'authority', 
    icon: 'clipboard-check',
    description: 'Audit and compliance verification'
  },
  [NODE_TYPES.DONOR]: { 
    label: 'Donor', 
    category: 'donation', 
    icon: 'hand-holding-heart',
    description: 'Contribution source'
  },
  [NODE_TYPES.FUND_POOL]: { 
    label: 'Fund Pool', 
    category: 'donation', 
    icon: 'piggy-bank',
    description: 'Aggregation layer for donations'
  },
  [NODE_TYPES.CAUSE]: { 
    label: 'Cause', 
    category: 'donation', 
    icon: 'ribbon',
    description: 'Program category or initiative'
  },
  [NODE_TYPES.EXECUTION_PARTNER]: { 
    label: 'Execution Partner', 
    category: 'donation', 
    icon: 'handshake',
    description: 'Service delivery partner'
  },
  [NODE_TYPES.WASTE_PRODUCER]: { 
    label: 'Waste Producer', 
    category: 'circularity', 
    icon: 'factory',
    description: 'Material source in circular economy'
  },
  [NODE_TYPES.COLLECTOR]: { 
    label: 'Collector', 
    category: 'circularity', 
    icon: 'truck',
    description: 'Material aggregation node'
  },
  [NODE_TYPES.RECYCLER]: { 
    label: 'Recycler', 
    category: 'circularity', 
    icon: 'recycle',
    description: 'Material transformation node'
  },
  [NODE_TYPES.BUYER]: { 
    label: 'Buyer', 
    category: 'circularity', 
    icon: 'shopping-cart',
    description: 'End consumer in supply chain'
  },
  [NODE_TYPES.SIGNAL_SOURCE]: { 
    label: 'Signal Source', 
    category: 'monitoring', 
    icon: 'signal',
    description: 'Telemetry data source'
  },
  [NODE_TYPES.TELEMETRY_NODE]: { 
    label: 'Telemetry Node', 
    category: 'monitoring', 
    icon: 'radar',
    description: 'Monitoring aggregation point'
  },
  [NODE_TYPES.CUSTOM]: { 
    label: 'Custom Node', 
    category: 'custom', 
    icon: 'cube',
    description: 'Custom participant type'
  }
};

// ============================================================================
// EDGE TYPE DEFINITIONS
// ============================================================================

/**
 * Blueprint Edge Types — All flow and dependency types
 */
export const EDGE_TYPES = {
  // Financial Flows
  FINANCIAL_FLOW: 'financial_flow',
  ISSUANCE: 'issuance',
  DISTRIBUTION: 'distribution',
  TRANSACTION: 'transaction',
  REDEMPTION: 'redemption',
  DISBURSEMENT: 'disbursement',
  
  // Governance
  POLICY_DEPENDENCY: 'policy_dependency',
  AUTHORITY_ESCALATION: 'authority_escalation',
  APPROVAL_CHAIN: 'approval_chain',
  
  // Monitoring
  SIGNAL_PROPAGATION: 'signal_propagation',
  TELEMETRY: 'telemetry',
  
  // Donation/CSR
  DONATION: 'donation',
  ALLOCATION: 'allocation',
  EXECUTION: 'execution',
  
  // Circularity
  COLLECTION: 'collection',
  PROCESSING: 'processing',
  SUPPLY: 'supply',
  
  // Dependencies
  DATA_DEPENDENCY: 'data_dependency',
  DEPENDENCY: 'dependency'
};

/**
 * Edge Type Metadata — Display names and visual styling
 */
export const EDGE_TYPE_METADATA = {
  [EDGE_TYPES.FINANCIAL_FLOW]: { 
    label: 'Financial Flow', 
    color: '#10B981', 
    flowType: 'money',
    description: 'Movement of funds between nodes'
  },
  [EDGE_TYPES.ISSUANCE]: { 
    label: 'Issuance', 
    color: '#059669', 
    flowType: 'money',
    description: 'Initial creation of programmable money'
  },
  [EDGE_TYPES.DISTRIBUTION]: { 
    label: 'Distribution', 
    color: '#34D399', 
    flowType: 'money',
    description: 'Allocation of funds to recipients'
  },
  [EDGE_TYPES.TRANSACTION]: { 
    label: 'Transaction', 
    color: '#6EE7B7', 
    flowType: 'money',
    description: 'Exchange of value for goods/services'
  },
  [EDGE_TYPES.REDEMPTION]: { 
    label: 'Redemption', 
    color: '#A7F3D0', 
    flowType: 'money',
    description: 'Conversion back to base currency'
  },
  [EDGE_TYPES.DISBURSEMENT]: { 
    label: 'Disbursement', 
    color: '#10B981', 
    flowType: 'money',
    description: 'Release of funds to beneficiaries'
  },
  [EDGE_TYPES.POLICY_DEPENDENCY]: { 
    label: 'Policy Dependency', 
    color: '#EFD6B8', 
    flowType: 'policy',
    description: 'Policy constraint relationship'
  },
  [EDGE_TYPES.AUTHORITY_ESCALATION]: { 
    label: 'Authority Escalation', 
    color: '#EF4444', 
    flowType: 'authority',
    description: 'Escalation path for decisions'
  },
  [EDGE_TYPES.APPROVAL_CHAIN]: { 
    label: 'Approval Chain', 
    color: '#F97316', 
    flowType: 'authority',
    description: 'Approval workflow sequence'
  },
  [EDGE_TYPES.SIGNAL_PROPAGATION]: { 
    label: 'Signal Propagation', 
    color: '#3B82F6', 
    flowType: 'signal',
    description: 'Telemetry signal flow'
  },
  [EDGE_TYPES.TELEMETRY]: { 
    label: 'Telemetry', 
    color: '#60A5FA', 
    flowType: 'signal',
    description: 'Monitoring data flow'
  },
  [EDGE_TYPES.DONATION]: { 
    label: 'Donation', 
    color: '#D4A88C', 
    flowType: 'money',
    description: 'Contribution from donor'
  },
  [EDGE_TYPES.ALLOCATION]: { 
    label: 'Allocation', 
    color: '#F472B6', 
    flowType: 'money',
    description: 'Distribution to causes'
  },
  [EDGE_TYPES.EXECUTION]: { 
    label: 'Execution', 
    color: '#FB7185', 
    flowType: 'service',
    description: 'Service delivery to beneficiary'
  },
  [EDGE_TYPES.COLLECTION]: { 
    label: 'Collection', 
    color: '#D7D2EA', 
    flowType: 'material',
    description: 'Material collection'
  },
  [EDGE_TYPES.PROCESSING]: { 
    label: 'Processing', 
    color: '#D7D2EA', 
    flowType: 'material',
    description: 'Material transformation'
  },
  [EDGE_TYPES.SUPPLY]: { 
    label: 'Supply', 
    color: '#C4B5FD', 
    flowType: 'material',
    description: 'Delivery to end consumer'
  },
  [EDGE_TYPES.DATA_DEPENDENCY]: { 
    label: 'Data Dependency', 
    color: '#6B7280', 
    flowType: 'data',
    description: 'Data flow between nodes'
  },
  [EDGE_TYPES.DEPENDENCY]: { 
    label: 'Dependency', 
    color: '#9CA3AF', 
    flowType: 'dependency',
    description: 'General dependency relationship'
  }
};

// ============================================================================
// BLUEPRINT GRAPH SCHEMA
// ============================================================================

/**
 * Create a new blueprint node
 */
export function createBlueprintNode({
  id,
  type,
  name,
  layer = 'default',
  position = { x: 0, y: 0 },
  participantRef = null,
  properties = {},
  signalRefs = []
}) {
  return {
    id: id || `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    name,
    layer,
    position,
    participantRef,
    properties: {
      capacity: properties.capacity || null,
      throughput: properties.throughput || null,
      balance: properties.balance || null,
      ...properties
    },
    signalRefs,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Create a new blueprint edge
 */
export function createBlueprintEdge({
  id,
  type,
  source,
  target,
  properties = {},
  policyRuleRefs = []
}) {
  const metadata = EDGE_TYPE_METADATA[type] || {};
  return {
    id: id || `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    source,
    target,
    properties: {
      flowType: metadata.flowType || 'dependency',
      capacity: properties.capacity || null,
      latency: properties.latency || null,
      weight: properties.weight || 1,
      ...properties
    },
    policyRuleRefs,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Create a new blueprint layer
 */
export function createBlueprintLayer({
  id,
  name,
  order = 0,
  color = '#6B7280',
  description = ''
}) {
  return {
    id: id || `layer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    order,
    color,
    description,
    createdAt: new Date().toISOString()
  };
}

/**
 * Create a new empty blueprint
 */
export function createEmptyBlueprint(programId) {
  return {
    id: `blueprint_${programId}`,
    programId,
    version: '1.0',
    nodes: [],
    edges: [],
    layers: [
      createBlueprintLayer({ id: 'layer_authority', name: 'Authority Layer', order: 0, color: '#EF4444' }),
      createBlueprintLayer({ id: 'layer_distribution', name: 'Distribution Layer', order: 1, color: '#10B981' }),
      createBlueprintLayer({ id: 'layer_participant', name: 'Participant Layer', order: 2, color: '#3B82F6' }),
      createBlueprintLayer({ id: 'layer_execution', name: 'Execution Layer', order: 3, color: '#D7D2EA' })
    ],
    
    // Engine references (to be populated by integration)
    cascadeEngineRef: null,
    exposureEngineRef: null,
    simulationEngineRef: null,
    monitoringRef: null,
    
    // Metadata
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

// ============================================================================
// BLUEPRINT TEMPLATES
// ============================================================================

/**
 * Pre-defined blueprint templates for common program types
 */
export const BLUEPRINT_TEMPLATES = {
  CBDC: {
    id: 'template_cbdc',
    name: 'CBDC Distribution',
    description: 'Central bank digital currency distribution network',
    category: 'financial',
    nodes: [
      { type: NODE_TYPES.CENTRAL_BANK, name: 'Central Bank', layer: 'layer_authority', position: { x: 400, y: 50 } },
      { type: NODE_TYPES.COMMERCIAL_BANK, name: 'Commercial Bank', layer: 'layer_distribution', position: { x: 400, y: 150 } },
      { type: NODE_TYPES.PROGRAM_SPONSOR, name: 'Program Sponsor', layer: 'layer_distribution', position: { x: 400, y: 250 } },
      { type: NODE_TYPES.BENEFICIARY, name: 'Beneficiary', layer: 'layer_participant', position: { x: 250, y: 350 } },
      { type: NODE_TYPES.MERCHANT, name: 'Merchant', layer: 'layer_execution', position: { x: 550, y: 350 } }
    ],
    edges: [
      { type: EDGE_TYPES.ISSUANCE, source: 0, target: 1 },
      { type: EDGE_TYPES.DISTRIBUTION, source: 1, target: 2 },
      { type: EDGE_TYPES.DISBURSEMENT, source: 2, target: 3 },
      { type: EDGE_TYPES.TRANSACTION, source: 3, target: 4 }
    ]
  },
  
  DONATION: {
    id: 'template_donation',
    name: 'Donation Program',
    description: 'Charitable donation distribution network',
    category: 'donation',
    nodes: [
      { type: NODE_TYPES.DONOR, name: 'Donor', layer: 'layer_authority', position: { x: 400, y: 50 } },
      { type: NODE_TYPES.FUND_POOL, name: 'Fund Pool', layer: 'layer_distribution', position: { x: 400, y: 150 } },
      { type: NODE_TYPES.CAUSE, name: 'Cause', layer: 'layer_distribution', position: { x: 400, y: 250 } },
      { type: NODE_TYPES.BENEFICIARY, name: 'Beneficiary', layer: 'layer_participant', position: { x: 250, y: 350 } },
      { type: NODE_TYPES.EXECUTION_PARTNER, name: 'Execution Partner', layer: 'layer_execution', position: { x: 550, y: 350 } }
    ],
    edges: [
      { type: EDGE_TYPES.DONATION, source: 0, target: 1 },
      { type: EDGE_TYPES.ALLOCATION, source: 1, target: 2 },
      { type: EDGE_TYPES.DISBURSEMENT, source: 2, target: 3 },
      { type: EDGE_TYPES.EXECUTION, source: 3, target: 4 }
    ]
  },
  
  EMPLOYEE_BENEFITS: {
    id: 'template_employee_benefits',
    name: 'Employee Benefits',
    description: 'Corporate employee benefits program',
    category: 'corporate',
    nodes: [
      { type: NODE_TYPES.PROGRAM_SPONSOR, name: 'Employer', layer: 'layer_authority', position: { x: 400, y: 50 } },
      { type: NODE_TYPES.COMMERCIAL_BANK, name: 'Benefits Bank', layer: 'layer_distribution', position: { x: 400, y: 150 } },
      { type: NODE_TYPES.BENEFICIARY, name: 'Employee', layer: 'layer_participant', position: { x: 400, y: 250 } },
      { type: NODE_TYPES.MERCHANT, name: 'Merchant Network', layer: 'layer_execution', position: { x: 400, y: 350 } }
    ],
    edges: [
      { type: EDGE_TYPES.DISBURSEMENT, source: 0, target: 1 },
      { type: EDGE_TYPES.DISTRIBUTION, source: 1, target: 2 },
      { type: EDGE_TYPES.TRANSACTION, source: 2, target: 3 }
    ]
  },
  
  CIRCULARITY: {
    id: 'template_circularity',
    name: 'Circularity Network',
    description: 'Circular economy supply chain',
    category: 'circularity',
    nodes: [
      { type: NODE_TYPES.WASTE_PRODUCER, name: 'Waste Producer', layer: 'layer_authority', position: { x: 400, y: 50 } },
      { type: NODE_TYPES.COLLECTOR, name: 'Collector', layer: 'layer_distribution', position: { x: 400, y: 150 } },
      { type: NODE_TYPES.RECYCLER, name: 'Recycler', layer: 'layer_distribution', position: { x: 400, y: 250 } },
      { type: NODE_TYPES.BUYER, name: 'Buyer', layer: 'layer_participant', position: { x: 400, y: 350 } }
    ],
    edges: [
      { type: EDGE_TYPES.COLLECTION, source: 0, target: 1 },
      { type: EDGE_TYPES.PROCESSING, source: 1, target: 2 },
      { type: EDGE_TYPES.SUPPLY, source: 2, target: 3 }
    ]
  },
  
  VC_FUNDING: {
    id: 'template_vc_funding',
    name: 'VC/PE Funding',
    description: 'Venture capital milestone-based funding',
    category: 'investment',
    nodes: [
      { type: NODE_TYPES.SPONSOR, name: 'VC Fund', layer: 'layer_authority', position: { x: 400, y: 50 } },
      { type: NODE_TYPES.AUTHORITY_NODE, name: 'Board Observer', layer: 'layer_authority', position: { x: 600, y: 50 } },
      { type: NODE_TYPES.BENEFICIARY, name: 'Portfolio Company', layer: 'layer_distribution', position: { x: 400, y: 150 } },
      { type: NODE_TYPES.MERCHANT, name: 'Vendor', layer: 'layer_participant', position: { x: 250, y: 250 } },
      { type: NODE_TYPES.BENEFICIARY, name: 'Employee', layer: 'layer_participant', position: { x: 550, y: 250 } }
    ],
    edges: [
      { type: EDGE_TYPES.DISBURSEMENT, source: 0, target: 2 },
      { type: EDGE_TYPES.AUTHORITY_ESCALATION, source: 2, target: 1 },
      { type: EDGE_TYPES.TRANSACTION, source: 2, target: 3 },
      { type: EDGE_TYPES.DISBURSEMENT, source: 2, target: 4 }
    ]
  }
};

// ============================================================================
// TOPOLOGY CALCULATIONS (for Commit Economy integration)
// ============================================================================

/**
 * Calculate topology metrics from blueprint for Commit Economy Engine
 */
export function calculateTopologyMetrics(blueprint) {
  if (!blueprint) return null;
  
  const nodes = blueprint.nodes || [];
  const edges = blueprint.edges || [];
  const layers = blueprint.layers || [];
  
  // Count nodes by category
  const beneficiaries = nodes.filter(n => n.type === NODE_TYPES.BENEFICIARY).length;
  const merchants = nodes.filter(n => n.type === NODE_TYPES.MERCHANT).length;
  const authorityNodes = nodes.filter(n => 
    [NODE_TYPES.AUTHORITY_NODE, NODE_TYPES.REGULATOR, NODE_TYPES.AUDITOR].includes(n.type)
  ).length;
  
  // Count unique layers used
  const uniqueLayers = new Set(nodes.map(n => n.layer)).size;
  
  // Calculate graph complexity (edges / nodes ratio)
  const graphDensity = nodes.length > 0 ? edges.length / nodes.length : 0;
  
  // Count signal attachment points
  const signalNodes = nodes.filter(n => n.signalRefs && n.signalRefs.length > 0).length;
  
  // Calculate edge type distribution
  const edgeTypeDistribution = edges.reduce((acc, edge) => {
    const flowType = EDGE_TYPE_METADATA[edge.type]?.flowType || 'unknown';
    acc[flowType] = (acc[flowType] || 0) + 1;
    return acc;
  }, {});
  
  return {
    // For Commit Economy Engine
    beneficiariesCount: beneficiaries,
    merchantsCount: merchants,
    authorityNodes: authorityNodes,
    programLayers: uniqueLayers,
    
    // Graph metrics
    totalNodes: nodes.length,
    totalEdges: edges.length,
    graphDensity: Math.round(graphDensity * 100) / 100,
    signalAttachmentPoints: signalNodes,
    
    // Edge distribution
    edgeTypeDistribution,
    
    // Layer metrics
    layerCount: layers.length,
    activeLayerCount: uniqueLayers
  };
}

/**
 * Get propagation paths from blueprint for Cascade Engine
 */
export function getPropagationPaths(blueprint, sourceNodeId) {
  if (!blueprint || !sourceNodeId) return [];
  
  const edges = blueprint.edges || [];
  const visited = new Set();
  const paths = [];
  
  function traverse(nodeId, path = []) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    
    const outgoingEdges = edges.filter(e => e.source === nodeId);
    
    for (const edge of outgoingEdges) {
      const newPath = [...path, { edge, targetNode: edge.target }];
      paths.push(newPath);
      traverse(edge.target, newPath);
    }
  }
  
  traverse(sourceNodeId);
  return paths;
}

/**
 * Get upstream dependencies for a node
 */
export function getUpstreamNodes(blueprint, nodeId) {
  if (!blueprint || !nodeId) return [];
  
  const edges = blueprint.edges || [];
  const nodes = blueprint.nodes || [];
  
  const upstreamEdges = edges.filter(e => e.target === nodeId);
  const upstreamNodeIds = upstreamEdges.map(e => e.source);
  
  return nodes.filter(n => upstreamNodeIds.includes(n.id));
}

/**
 * Get downstream dependents for a node
 */
export function getDownstreamNodes(blueprint, nodeId) {
  if (!blueprint || !nodeId) return [];
  
  const edges = blueprint.edges || [];
  const nodes = blueprint.nodes || [];
  
  const downstreamEdges = edges.filter(e => e.source === nodeId);
  const downstreamNodeIds = downstreamEdges.map(e => e.target);
  
  return nodes.filter(n => downstreamNodeIds.includes(n.id));
}

// ============================================================================
// CONTEXT
// ============================================================================

const SystemBlueprintContext = createContext(null);

/**
 * SystemBlueprintProvider — DERIVED from ProgramIntegrityContext
 * 
 * FOUNDER DIRECTIVE: SINGLE SOURCE OF TRUTH REQUIREMENT
 * - This context PROJECTS state from ProgramIntegrityContext for UI purposes
 * - All mutations propagate back to ProgramIntegrityContext
 * - No independent writable shadow state
 */
export function SystemBlueprintProvider({ children }) {
  // FOUNDER DIRECTIVE: Derive blueprint from ProgramIntegrityContext
  // If ProgramIntegrityContext is not available (e.g., in demo mode), 
  // fall back to local state for backwards compatibility
  // NOTE: Using useProgramIntegrityOptional() to avoid rules-of-hooks violation
  const programIntegrityContext = useProgramIntegrityOptional();
  
  // UI-only state (not data state)
  const [localBlueprint, setLocalBlueprint] = useState(null); // Fallback for demo mode
  const [blueprintHistory, setBlueprintHistory] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  
  // FOUNDER DIRECTIVE: activeBlueprint is DERIVED from ProgramIntegrityContext
  // or falls back to local state for demo/standalone usage
  const activeBlueprint = useMemo(() => {
    if (programIntegrityContext?.integrity?.systemBlueprint) {
      return programIntegrityContext.integrity.systemBlueprint;
    }
    return localBlueprint;
  }, [programIntegrityContext?.integrity?.systemBlueprint, localBlueprint]);
  
  // Internal setter that propagates to ProgramIntegrityContext or local state
  const setActiveBlueprint = useCallback((blueprintOrUpdater) => {
    const newBlueprint = typeof blueprintOrUpdater === 'function' 
      ? blueprintOrUpdater(activeBlueprint)
      : blueprintOrUpdater;
    
    if (programIntegrityContext) {
      // FOUNDER DIRECTIVE: Propagate to canonical owner
      programIntegrityContext.updateBlueprint(newBlueprint);
    } else {
      // Fallback for demo mode
      setLocalBlueprint(newBlueprint);
    }
  }, [activeBlueprint, programIntegrityContext]);
  
  // ============================================================================
  // BLUEPRINT MANAGEMENT
  // ============================================================================
  
  /**
   * Initialize a new blueprint for a program
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const initializeBlueprint = useCallback((programId, templateId = null) => {
    let blueprint;
    
    if (templateId && BLUEPRINT_TEMPLATES[templateId]) {
      // Create from template
      const template = BLUEPRINT_TEMPLATES[templateId];
      blueprint = createEmptyBlueprint(programId);
      
      // Add template nodes with generated IDs
      const nodeIdMap = {};
      template.nodes.forEach((nodeTemplate, index) => {
        const node = createBlueprintNode({
          ...nodeTemplate,
          name: nodeTemplate.name,
          type: nodeTemplate.type,
          layer: nodeTemplate.layer,
          position: nodeTemplate.position
        });
        nodeIdMap[index] = node.id;
        blueprint.nodes.push(node);
      });
      
      // Add template edges with mapped IDs
      template.edges.forEach(edgeTemplate => {
        const edge = createBlueprintEdge({
          type: edgeTemplate.type,
          source: nodeIdMap[edgeTemplate.source],
          target: nodeIdMap[edgeTemplate.target]
        });
        blueprint.edges.push(edge);
      });
    } else {
      // Create empty blueprint
      blueprint = createEmptyBlueprint(programId);
    }
    
    setActiveBlueprint(blueprint);
    setBlueprintHistory([]);
    return blueprint;
  }, [setActiveBlueprint]);
  
  /**
   * Load an existing blueprint
   */
  const loadBlueprint = useCallback((blueprint) => {
    setActiveBlueprint(blueprint);
    setBlueprintHistory([]);
    setSelectedNode(null);
    setSelectedEdge(null);
  }, [setActiveBlueprint]);
  
  /**
   * Save current blueprint state to history (for undo)
   */
  const saveToHistory = useCallback(() => {
    if (activeBlueprint) {
      setBlueprintHistory(prev => [...prev.slice(-19), JSON.parse(JSON.stringify(activeBlueprint))]);
    }
  }, [activeBlueprint]);
  
  /**
   * Undo last change
   */
  const undoChange = useCallback(() => {
    if (blueprintHistory.length > 0) {
      const previousState = blueprintHistory[blueprintHistory.length - 1];
      setBlueprintHistory(prev => prev.slice(0, -1));
      setActiveBlueprint(previousState);
    }
  }, [blueprintHistory, setActiveBlueprint]);
  
  // ============================================================================
  // NODE OPERATIONS
  // ============================================================================
  
  /**
   * Add a node to the blueprint
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const addNode = useCallback((nodeData) => {
    if (!activeBlueprint) return null;
    
    saveToHistory();
    
    const node = createBlueprintNode(nodeData);
    
    setActiveBlueprint(prev => ({
      ...prev,
      nodes: [...prev.nodes, node],
      updatedAt: new Date().toISOString()
    }));
    
    return node;
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  /**
   * Update a node
   */
  const updateNode = useCallback((nodeId, updates) => {
    if (!activeBlueprint) return;
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      nodes: prev.nodes.map(n => 
        n.id === nodeId 
          ? { ...n, ...updates, updatedAt: new Date().toISOString() }
          : n
      ),
      updatedAt: new Date().toISOString()
    }));
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  /**
   * Remove a node and its connected edges
   */
  const removeNode = useCallback((nodeId) => {
    if (!activeBlueprint) return;
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      nodes: prev.nodes.filter(n => n.id !== nodeId),
      edges: prev.edges.filter(e => e.source !== nodeId && e.target !== nodeId),
      updatedAt: new Date().toISOString()
    }));
    
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
  }, [activeBlueprint, saveToHistory, selectedNode, setActiveBlueprint]);
  
  // ============================================================================
  // EDGE OPERATIONS
  // ============================================================================
  
  /**
   * Add an edge to the blueprint
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const addEdge = useCallback((edgeData) => {
    if (!activeBlueprint) return null;
    
    // Validate that source and target nodes exist
    const sourceExists = activeBlueprint.nodes.some(n => n.id === edgeData.source);
    const targetExists = activeBlueprint.nodes.some(n => n.id === edgeData.target);
    
    if (!sourceExists || !targetExists) {
      console.error('Cannot create edge: source or target node does not exist');
      return null;
    }
    
    // Check for duplicate edge
    const duplicateExists = activeBlueprint.edges.some(
      e => e.source === edgeData.source && e.target === edgeData.target && e.type === edgeData.type
    );
    
    if (duplicateExists) {
      console.warn('Duplicate edge already exists');
      return null;
    }
    
    saveToHistory();
    
    const edge = createBlueprintEdge(edgeData);
    
    setActiveBlueprint(prev => ({
      ...prev,
      edges: [...prev.edges, edge],
      updatedAt: new Date().toISOString()
    }));
    
    return edge;
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  /**
   * Update an edge
   */
  const updateEdge = useCallback((edgeId, updates) => {
    if (!activeBlueprint) return;
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      edges: prev.edges.map(e => 
        e.id === edgeId 
          ? { ...e, ...updates, updatedAt: new Date().toISOString() }
          : e
      ),
      updatedAt: new Date().toISOString()
    }));
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  /**
   * Remove an edge
   */
  const removeEdge = useCallback((edgeId) => {
    if (!activeBlueprint) return;
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      edges: prev.edges.filter(e => e.id !== edgeId),
      updatedAt: new Date().toISOString()
    }));
    
    if (selectedEdge?.id === edgeId) {
      setSelectedEdge(null);
    }
  }, [activeBlueprint, saveToHistory, selectedEdge, setActiveBlueprint]);
  
  // ============================================================================
  // LAYER OPERATIONS
  // ============================================================================
  
  /**
   * Add a layer to the blueprint
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const addLayer = useCallback((layerData) => {
    if (!activeBlueprint) return null;
    
    saveToHistory();
    
    const layer = createBlueprintLayer(layerData);
    
    setActiveBlueprint(prev => ({
      ...prev,
      layers: [...prev.layers, layer],
      updatedAt: new Date().toISOString()
    }));
    
    return layer;
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  /**
   * Update a layer
   */
  const updateLayer = useCallback((layerId, updates) => {
    if (!activeBlueprint) return;
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      layers: prev.layers.map(l => 
        l.id === layerId 
          ? { ...l, ...updates }
          : l
      ),
      updatedAt: new Date().toISOString()
    }));
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  /**
   * Remove a layer (moves nodes to default layer)
   */
  const removeLayer = useCallback((layerId) => {
    if (!activeBlueprint) return;
    if (layerId === 'default') return; // Cannot remove default layer
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      layers: prev.layers.filter(l => l.id !== layerId),
      nodes: prev.nodes.map(n => 
        n.layer === layerId 
          ? { ...n, layer: 'default' }
          : n
      ),
      updatedAt: new Date().toISOString()
    }));
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  // ============================================================================
  // SIGNAL ATTACHMENT
  // ============================================================================
  
  /**
   * Attach a signal to a node
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const attachSignalToNode = useCallback((nodeId, signalRef) => {
    if (!activeBlueprint) return;
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      nodes: prev.nodes.map(n => 
        n.id === nodeId 
          ? { 
              ...n, 
              signalRefs: [...(n.signalRefs || []), signalRef],
              updatedAt: new Date().toISOString()
            }
          : n
      ),
      updatedAt: new Date().toISOString()
    }));
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  /**
   * Detach a signal from a node
   * FOUNDER DIRECTIVE: Updates propagate to ProgramIntegrityContext
   */
  const detachSignalFromNode = useCallback((nodeId, signalRef) => {
    if (!activeBlueprint) return;
    
    saveToHistory();
    
    setActiveBlueprint(prev => ({
      ...prev,
      nodes: prev.nodes.map(n => 
        n.id === nodeId 
          ? { 
              ...n, 
              signalRefs: (n.signalRefs || []).filter(s => s !== signalRef),
              updatedAt: new Date().toISOString()
            }
          : n
      ),
      updatedAt: new Date().toISOString()
    }));
  }, [activeBlueprint, saveToHistory, setActiveBlueprint]);
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  /**
   * Topology metrics for Commit Economy integration
   */
  const topologyMetrics = useMemo(() => {
    return calculateTopologyMetrics(activeBlueprint);
  }, [activeBlueprint]);
  
  /**
   * Validation status
   */
  const validationStatus = useMemo(() => {
    if (!activeBlueprint) return { isValid: false, errors: ['No blueprint loaded'] };
    
    const errors = [];
    const warnings = [];
    
    // Check minimum nodes
    if (activeBlueprint.nodes.length < 2) {
      errors.push('Blueprint must have at least 2 nodes');
    }
    
    // Check minimum edges
    if (activeBlueprint.edges.length < 1) {
      errors.push('Blueprint must have at least 1 edge connecting nodes');
    }
    
    // Check for orphan nodes (nodes with no edges)
    const connectedNodeIds = new Set();
    activeBlueprint.edges.forEach(e => {
      connectedNodeIds.add(e.source);
      connectedNodeIds.add(e.target);
    });
    const orphanNodes = activeBlueprint.nodes.filter(n => !connectedNodeIds.has(n.id));
    if (orphanNodes.length > 0) {
      warnings.push(`${orphanNodes.length} node(s) have no connections`);
    }
    
    // Check for required node types based on program type
    // (This could be expanded based on program requirements)
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      nodeCount: activeBlueprint.nodes.length,
      edgeCount: activeBlueprint.edges.length,
      connectedNodes: connectedNodeIds.size,
      orphanNodes: orphanNodes.length
    };
  }, [activeBlueprint]);
  
  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // Constants
    NODE_TYPES,
    NODE_TYPE_METADATA,
    EDGE_TYPES,
    EDGE_TYPE_METADATA,
    BLUEPRINT_TEMPLATES,
    
    // State
    activeBlueprint,
    isEditing,
    selectedNode,
    selectedEdge,
    blueprintHistory,
    
    // Computed
    topologyMetrics,
    validationStatus,
    
    // Blueprint management
    initializeBlueprint,
    loadBlueprint,
    undoChange,
    setIsEditing,
    setSelectedNode,
    setSelectedEdge,
    
    // Node operations
    addNode,
    updateNode,
    removeNode,
    
    // Edge operations
    addEdge,
    updateEdge,
    removeEdge,
    
    // Layer operations
    addLayer,
    updateLayer,
    removeLayer,
    
    // Signal operations
    attachSignalToNode,
    detachSignalFromNode,
    
    // Utility functions
    calculateTopologyMetrics,
    getPropagationPaths,
    getUpstreamNodes,
    getDownstreamNodes,
    createBlueprintNode,
    createBlueprintEdge,
    createBlueprintLayer
  };
  
  return (
    <SystemBlueprintContext.Provider value={value}>
      {children}
    </SystemBlueprintContext.Provider>
  );
}

export function useSystemBlueprint() {
  const context = useContext(SystemBlueprintContext);
  if (!context) {
    throw new Error('useSystemBlueprint must be used within SystemBlueprintProvider');
  }
  return context;
}

export default SystemBlueprintContext;
